import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '@lib/core/src/components/Button';
import TextInput from '@lib/core/src/components/TextInput';
import palette from '@lib/core/src/theme/palette';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Stack } from '@mui/material';

import { Mfa } from '@/api/generated';
import useLoginFlowContext from '@/context/LoginFlowContextProvider/useLoginContext';
import RoutesMap from '@/enums/RoutesMap';

import {
  ClickHere,
  Container,
  ForgotPassword,
  InputAndTitleContainer,
  InputContainer,
  InputError,
  InputsContainer,
  InputTitle,
  Title,
  TitleDescription,
  TitlesContainer
} from './LoginStyles';

const Login = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaDetails, setMfaDetails] = useState<Mfa | undefined>(undefined);
  const [mfaCode, setMfaCode] = useState<string>('');

  const {
    signIn,
    signInLoading,
    mfaSignIn,
    isLoadingAuthMfaSignin,
    isAuthenticated,
    userDetailsLoading,
    currentUser
  } = useLoginFlowContext();
  const navigate = useNavigate();

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value && !emailRegex.test(value)) {
      setEmailError('Invalid email address');
    } else {
      setEmailError('');
    }
  }, []);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$_!%*#?&]{8,}$/;
    if (value && !passwordRegex.test(value)) {
      setPasswordError(
        'Password must be at least 8 characters long and include at least one letter and one number'
      );
    } else {
      setPasswordError('');
    }
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPassword(prevShowPassword => !prevShowPassword);
  }, []);

  const handleMfaSignIn = useCallback(async () => {
    try {
      await mfaSignIn({
        challengeName: mfaDetails?.challengeName,
        code: mfaCode,
        userId: mfaDetails?.userId,
        session: mfaDetails?.session
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.response?.status === 401) {
        setPasswordError('Incorrect verification code');
      } else {
        setPasswordError('Something went wrong. Please try again later');
      }
    }
  }, [mfaDetails, mfaCode, mfaSignIn]);

  useEffect(() => {
    if (isAuthenticated && currentUser && !userDetailsLoading) {
      navigate(RoutesMap.Home);
    }
  }, [isAuthenticated, navigate, currentUser, userDetailsLoading]);

  const isDisabled = !!emailError || !!passwordError || !email || !password;

  const handleSignIn = useCallback(async () => {
    if (!isDisabled) {
      const signInDetails = await signIn({ email, password });
      if (signInDetails?.mfa?.session) {
        setMfaDetails(signInDetails.mfa);
      }
    }
  }, [isDisabled, signIn, email, password]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !isDisabled) {
        handleSignIn();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDisabled, handleSignIn]);

  return (
    <Container>
      {mfaDetails ? (
        <Stack gap={4}>
          <Title>Multi Factor Authentication</Title>
          <InputContainer>
            <TextInput
              type="text"
              placeholder="Enter code"
              value={mfaCode}
              onChange={event => setMfaCode(event.currentTarget.value)}
            />
          </InputContainer>
          <Button
            sx={{ marginTop: '48px', height: '40px' }}
            text="Continue"
            onClick={handleMfaSignIn}
            loading={isLoadingAuthMfaSignin}
            disabled={!mfaCode}
          />
        </Stack>
      ) : (
        <Stack justifyContent="center" alignItems="center" width="100%">
          <TitlesContainer>
            <Title>Welcome back</Title>
            <TitleDescription>Log in to your account to get started</TitleDescription>
          </TitlesContainer>
          <InputsContainer>
            <InputAndTitleContainer>
              <InputTitle>Email</InputTitle>
              <Stack>
                <InputContainer>
                  <TextInput
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={handleEmailChange}
                  />
                </InputContainer>
                {emailError && <InputError>{emailError}</InputError>}
              </Stack>
            </InputAndTitleContainer>
            <InputAndTitleContainer>
              <InputTitle>Password</InputTitle>
              <Stack>
                <InputContainer>
                  <TextInput
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={handlePasswordChange}
                  />
                  <span
                    onClick={toggleShowPassword}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '8px',
                      zIndex: 15
                    }}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon
                        sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                      />
                    ) : (
                      <VisibilityOutlinedIcon
                        sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                      />
                    )}
                  </span>
                </InputContainer>
                {passwordError && <InputError>{passwordError}</InputError>}
              </Stack>
            </InputAndTitleContainer>
          </InputsContainer>
          <Button
            sx={{ marginTop: '48px' }}
            text="Log in"
            onClick={handleSignIn}
            loading={signInLoading}
            disabled={isDisabled}
          />
          <Stack direction="row" gap="4px" marginTop="80px">
            <ForgotPassword>Forgot your password?</ForgotPassword>
            <ClickHere
              onClick={() => {
                navigate(RoutesMap.ForgotPassword);
              }}
            >
              Click here
            </ClickHere>
          </Stack>
        </Stack>
      )}
    </Container>
  );
};

export default Login;
